import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Inbox as InboxIcon, ExternalLink } from "lucide-react";
import Shell from "../components/Shell";
import { useApp } from "../context/AppContext";
import { endpoints } from "../lib/api";
import { ProjectDetailsFields, detailsPayload, saveErrorText } from "../components/ProjectDetailsFields";

const STAGES = ["Idea", "Prototype", "MVP", "Testnet / beta", "Live"];
const TRACKS2 = ["AI", "Web3", "AI, Web3"];
const TYPE_LABEL = { pledge: "Pledges", inkind: "Offers", message: "Messages" };

export default function BuilderPage() {
  const { auth } = useApp();
  const [data, setData] = React.useState(null);
  const [edit, setEdit] = React.useState(null);
  const [inbox, setInbox] = React.useState(null);

  const load = () => endpoints.builderMe().then((d) => setData(d)).catch(() => setData({ projects: [] }));
  React.useEffect(() => { if (auth.role === "builder") load(); }, [auth.role]);

  if (auth.loading) return <Shell><div className="flex flex-1 items-center justify-center text-on-dark-muted">Loading…</div></Shell>;
  if (!auth.authenticated) {
    return <Shell><div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-center">
      <h1 className="font-display text-[26px] font-bold text-white">Builder sign-in</h1>
      <p className="text-on-dark-muted">Sign in with your team email to manage your project.</p>
      <Link to="/signin" className="rounded-pill bg-gold-500 px-6 py-3 font-bold text-navy-900">Go to sign in</Link>
    </div></Shell>;
  }
  if (auth.role !== "builder") {
    return <Shell><div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center" data-testid="builder-denied">
      <h1 className="font-display text-[26px] font-bold text-white">No builder access</h1>
      <p className="text-on-dark-muted">This email isn't linked to a project builder account.</p>
    </div></Shell>;
  }

  return (
    <Shell footer={false}>
      <div className="mx-auto w-full max-w-4xl px-4 py-6">
        <h1 className="font-display text-[28px] font-extrabold text-white">My projects</h1>
        <p className="mb-6 text-[13px] text-on-dark-muted">Signed in as {auth.email} · <span className="font-semibold text-gold-400">builder</span></p>

        {!data ? <p className="text-on-dark-muted">Loading…</p> : data.projects.length === 0 ? (
          <div className="rounded-lg bg-white/5 p-8 text-center ring-1 ring-white/10" data-testid="builder-empty">
            <p className="text-white">No project is linked to your email yet.</p>
            <p className="mt-1 text-[13px] text-on-dark-muted">Ask an organiser to add your email to your project's team contacts.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="builder-projects">
            {data.projects.map((p) => (
              <div key={p.id} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-14 overflow-hidden rounded-lg bg-navy-100">
                      {p.hero_card_url && <img src={p.hero_card_url} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div>
                      <p className="font-display text-[18px] font-bold text-white">{p.project}</p>
                      <p className="text-[13px] text-on-dark-muted">{p.stage} · {p.track}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/p/${p.slug}`} target="_blank" className="flex items-center gap-1.5 rounded-pill bg-white/10 px-3 py-2 text-[13px] font-semibold text-white"><ExternalLink size={14} /> View</Link>
                    <button onClick={() => setInbox(p)} data-testid={`builder-inbox-${p.slug}`} className="flex items-center gap-1.5 rounded-pill bg-white/10 px-3 py-2 text-[13px] font-semibold text-white"><InboxIcon size={14} /> Inbox ({p.counts.pledges + p.counts.offers + p.counts.messages})</button>
                    <button onClick={() => setEdit(p)} data-testid={`builder-edit-${p.slug}`} className="flex items-center gap-1.5 rounded-pill bg-gold-500 px-4 py-2 text-[13px] font-bold text-navy-900"><Pencil size={14} /> Edit</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {edit && <BuilderEditModal project={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {inbox && <BuilderInboxModal project={inbox} onClose={() => setInbox(null)} />}
    </Shell>
  );
}

function BuilderEditModal({ project, onClose, onSaved }) {
  const [full, setFull] = React.useState(null);
  const [hero, setHero] = React.useState(null);
  const [heroCard, setHeroCard] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const fileRef = React.useRef();
  const cardRef = React.useRef();

  React.useEffect(() => {
    let live = true;
    endpoints.builderGetProject(project.id).then((p) => { if (!live) return; setFull(p); setHero(p.hero_image_url); setHeroCard(p.hero_card_url); }).catch(() => {});
    return () => { live = false; };
  }, [project.id]);

  const save = async () => {
    setSaving(true);
    try {
      await endpoints.builderEditProject(project.id, {
        project: full.project, one_liner: full.one_liner, press_release: full.press_release,
        description: full.description, stage: full.stage, track: full.track, ...detailsPayload(full),
      });
      toast("Project updated");
      onSaved();
    } catch (ex) { toast(saveErrorText(ex)); } finally { setSaving(false); }
  };

  const upload = async (e, kind) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const r = await endpoints.builderUploadHero(project.id, file, kind);
      if (kind === "card") setHeroCard(r.hero_card_url); else setHero(r.hero_image_url);
      toast("Image uploaded");
    } catch { toast("Upload failed"); }
    e.target.value = "";
  };
  const clearHero = async (kind) => {
    await endpoints.builderClearHero(project.id, kind);
    if (kind === "card") setHeroCard(null); else setHero(null);
    toast("Reset to topic art");
  };

  const F = ({ label, children }) => (<label className="block"><span className="mb-1 block text-[13px] font-semibold text-ink-900">{label}</span>{children}</label>);
  const inp = "w-full rounded-md border border-line-200 bg-white px-3 py-2 text-[14px] text-ink-900 outline-none focus:border-gold-500";

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" data-testid="builder-edit-modal">
      <div className="absolute inset-0 bg-navy-950/60" onClick={onClose} />
      <div className="scroll-area relative z-10 max-h-[92vh] w-full max-w-[560px] overflow-y-auto rounded-t-sheet bg-surface-50 p-5 sm:rounded-sheet">
        <h3 className="mb-4 font-display text-[20px] font-bold text-navy-900">Edit {project.project}</h3>
        {!full ? <p className="text-ink-600">Loading…</p> : (
          <div className="space-y-3">
            <F label="Project name"><input className={inp} value={full.project} onChange={(e) => setFull({ ...full, project: e.target.value })} /></F>
            <F label="One-liner"><input className={inp} value={full.one_liner} onChange={(e) => setFull({ ...full, one_liner: e.target.value })} /></F>
            <div className="flex gap-3">
              <F label="Stage"><select className={inp} value={full.stage} onChange={(e) => setFull({ ...full, stage: e.target.value })}>{STAGES.map((s) => <option key={s}>{s}</option>)}</select></F>
              <F label="Track"><select className={inp} value={full.track} onChange={(e) => setFull({ ...full, track: e.target.value })}>{TRACKS2.map((s) => <option key={s}>{s}</option>)}</select></F>
            </div>
            <F label="Press release"><textarea rows={4} className={inp} value={full.press_release} onChange={(e) => setFull({ ...full, press_release: e.target.value })} /></F>
            <F label="Description"><textarea rows={3} className={inp} value={full.description} onChange={(e) => setFull({ ...full, description: e.target.value })} /></F>
            <ProjectDetailsFields full={full} setFull={setFull} inp={inp} prefix="builder-edit" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-line-200 bg-white p-3">
                <span className="mb-1 block text-[13px] font-semibold text-ink-900">Swipe hero card</span>
                <p className="mb-2 text-[11px] text-ink-400">Shows on the swipe card. Recommended 1200 × 1200 px (1:1, square). Name/branding can be baked in.</p>
                <div className="mb-2 aspect-square w-full overflow-hidden rounded-md bg-navy-100">{heroCard && <img src={heroCard} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex items-center gap-3">
                  <input ref={cardRef} type="file" accept="image/*" onChange={(e) => upload(e, "card")} className="hidden" data-testid="builder-hero-card-file" />
                  <button onClick={() => cardRef.current.click()} data-testid="builder-upload-hero-card" className="rounded-pill bg-navy-800 px-4 py-2 text-[13px] font-semibold text-white">Upload</button>
                  {heroCard && <button onClick={() => clearHero("card")} className="text-[13px] font-semibold text-coral-500">Reset</button>}
                </div>
              </div>
              <div className="rounded-md border border-line-200 bg-white p-3">
                <span className="mb-1 block text-[13px] font-semibold text-ink-900">Project page hero</span>
                <p className="mb-2 text-[11px] text-ink-400">Landscape, shows on the project page & grid. Recommended 1600 × 900 px (16:9), no baked-in text.</p>
                <div className="mb-2 aspect-video w-full overflow-hidden rounded-md bg-navy-100">{hero && <img src={hero} alt="" className="h-full w-full object-cover" />}</div>
                <div className="flex items-center gap-3">
                  <input ref={fileRef} type="file" accept="image/*" onChange={(e) => upload(e, "page")} className="hidden" data-testid="builder-hero-file" />
                  <button onClick={() => fileRef.current.click()} data-testid="builder-upload-hero-page" className="rounded-pill bg-navy-800 px-4 py-2 text-[13px] font-semibold text-white">Upload</button>
                  {hero && <button onClick={() => clearHero("page")} className="text-[13px] font-semibold text-coral-500">Reset</button>}
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={onClose} className="rounded-pill border border-line-200 bg-white px-5 py-2.5 font-semibold text-ink-900">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="builder-edit-save" className="flex-1 rounded-pill bg-gold-500 py-2.5 font-bold text-navy-900 disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BuilderInboxModal({ project, onClose }) {
  const [type, setType] = React.useState("pledge");
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    setItems(null);
    endpoints.builderInbox(project.id, type).then((d) => setItems(d.items)).catch(() => setItems([]));
  }, [project.id, type]);

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" data-testid="builder-inbox-modal">
      <div className="absolute inset-0 bg-navy-950/60" onClick={onClose} />
      <div className="scroll-area relative z-10 max-h-[92vh] w-full max-w-[560px] overflow-y-auto rounded-t-sheet bg-navy-900 p-5 ring-1 ring-white/10 sm:rounded-sheet">
        <h3 className="mb-1 font-display text-[20px] font-bold text-white">{project.project} inbox</h3>
        <p className="mb-4 text-[12px] text-on-dark-muted">Read-only. Organisers manage status and follow-ups.</p>
        <div className="mb-4 flex gap-2">
          {Object.keys(TYPE_LABEL).map((t) => (
            <button key={t} onClick={() => setType(t)} className={`rounded-pill px-4 py-1.5 text-[13px] font-bold ${type === t ? "bg-gold-500 text-navy-900" : "bg-white/5 text-white"}`}>{TYPE_LABEL[t]}</button>
          ))}
        </div>
        {items === null ? <p className="text-on-dark-muted">Loading…</p> : items.length === 0 ? (
          <p className="py-10 text-center text-on-dark-muted">Nothing here yet ✨</p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="rounded-lg bg-white/5 p-4 ring-1 ring-white/10">
                <p className="text-[13px] text-on-dark-muted">{it.is_anonymous ? "Anonymous" : it.name}{it.contact_email ? ` · ${it.contact_email}` : ""}</p>
                <p className="mt-1 text-[14px] text-white/90">
                  {type === "pledge" && <span className="font-num">{it.currency} {it.amount}</span>}
                  {type === "inkind" && <span>{(it.categories || []).join(", ")}{it.organisation ? ` · ${it.organisation}` : ""}</span>}
                  {type === "message" && <span>{it.subject ? `${it.subject}: ` : ""}{it.body}</span>}
                  {(it.note || it.details) && <span className="block text-on-dark-muted">{it.note || it.details}</span>}
                </p>
                <span className="mt-2 inline-block rounded-pill bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-on-dark-muted">{it.status}</span>
              </div>
            ))}
          </div>
        )}
        <button onClick={onClose} className="mt-5 w-full rounded-pill border border-white/15 py-2.5 font-semibold text-white">Close</button>
      </div>
    </div>
  );
}
